import { last } from "lodash-es";
import moment from "moment-timezone";
import { SchedulerStoreType } from "../../state/scheduler.store";
import { getRoosterAutobookAvailabilityMeta } from "../../services/api/meeting.api";
import type { MeetingAvailabilityRange } from "../../state/scheduler.store";
import { AvailabilityAutobookLinkDataApiResponse } from "../../services/api/types/autobookAvailability.type";
import { getLinkType } from "../link";
import {
  setLinkHasExpiredError,
  setUserGrantAsInvalidError
} from "../../utils/errorManagement";
import { TFunction } from "i18next"; // Add this import if not already present

export const handleAutobookLink = async (
  actions: SchedulerStoreType["actions"],
  autobookAvailabilityData: AvailabilityAutobookLinkDataApiResponse,
  linkType: ReturnType<typeof getLinkType>,
  t: TFunction,
  requestSessionStartedAt = Date.now()
) => {
  if (!linkType) {
    return;
  }
  const autobookMeta = await getRoosterAutobookAvailabilityMeta({
    linkId: linkType.linkId
  });

  if (
    !autobookMeta?.isSenderGrantValid &&
    autobookAvailabilityData?.docData?.client
  ) {
    // If the sender grant is invalid and there is worker data, mark the sender grant as invalid.
    setUserGrantAsInvalidError(actions, t);
    return;
  }

  if (autobookAvailabilityData?.docData?.userId) {
    actions.setLink(
      autobookAvailabilityData?.docData.userId,
      null,
      linkType.linkId,
      null
    );
  }

  if (autobookAvailabilityData?.docData?.client) {
    actions.setClient("normal", autobookAvailabilityData.docData.client);
    actions.setUiState({
      currentView: "month",
      currentStep: "scheduled"
    });
  }

  actions.setUser(
    autobookAvailabilityData?.docData.userId ?? null,
    autobookAvailabilityData?.docData.user?.name ?? "",
    ""
  );
  actions.setHost(
    autobookAvailabilityData?.docData?.user?.name ?? "",
    autobookAvailabilityData?.docData?.user?.email ?? ""
  );

  const interviewersFilter =
    autobookAvailabilityData?.docData?.interviewersFilter ?? null;
  const meetingHasCompositeFilter = interviewersFilter !== null;

  const interviewers = autobookAvailabilityData?.docData?.interviewers?.map(
    (i) => ({
      name:
        autobookAvailabilityData?.docData?.workerData?.find(
          (w) => w.email === i || w.value === i
        )?.name ?? i,
      value: i,
      email: i
    })
  );

  const availabilityWindowMins = autobookAvailabilityData?.docData?.end ?? 0;
  const duration = autobookAvailabilityData?.docData?.duration ?? 30;
  const availabilityTimezone =
    autobookAvailabilityData?.docData?.timeZone ?? moment.tz.guess();

  const range = calculateRange(
    autobookAvailabilityData,
    availabilityTimezone,
    availabilityWindowMins
  );

  actions.setMeetingLink({
    id: linkType?.linkId,
    hasDynamicInterviewersFilter: meetingHasCompositeFilter,
    type: "rooster",
    mode: "autobook",
    jobReqId: autobookAvailabilityData?.docData?.jobReqId,
    availability: {
      type:
        autobookAvailabilityData?.docData?.selectionMode === "individual_dates"
          ? "individual_dates"
          : "range",
      availabilityWindowMins,
      duration,
      autobookIntervalFreq:
        autobookAvailabilityData?.docData?.slotStartIncrement ?? 30,
      range
    },
    interviewers,
    interviewersFilter: interviewersFilter,
    isDisabled: false,
    showTentativeAsBusy: autobookMeta?.showTentativeAsBusy ?? true,
    scheduledData: {
      form: {
        firstName: autobookAvailabilityData?.docData?.candidate?.firstName,
        lastName: autobookAvailabilityData?.docData?.candidate?.lastName,
        email: autobookAvailabilityData?.docData?.candidate?.email,
        telephone: `+${autobookAvailabilityData?.docData?.candidate?.countryCode}${autobookAvailabilityData?.docData?.candidate?.telephone}`
      },
      docDataRooster: autobookAvailabilityData?.docData,
      availabilityDocData: autobookAvailabilityData?.availabilityDocData ?? {},
      alreadyScheduled: false
    },
    canCancel: autobookAvailabilityData?.docData?.cancel ?? false,
    canReschedule: autobookAvailabilityData?.docData?.reschedule ?? false,
    rescheduleStatus:
      autobookAvailabilityData?.docData?.rescheduleStatus ?? null
  });

  const prevSelectedSlot = autobookAvailabilityData?.docData.selected;

  let expiredLink = false;
  if (moment(last(range)?.rangeEnd).isBefore(moment())) {
    if (prevSelectedSlot?.length === 0 || !prevSelectedSlot) {
      expiredLink = true;
      setLinkHasExpiredError(actions, "autobook", t);
      return;
    }
    expiredLink = true;
  }

  const isRescheduleFlow = !!(prevSelectedSlot && prevSelectedSlot?.length > 0);
  const isCancelled = autobookAvailabilityData?.docData?.cancelled ?? false;

  actions.setSessionData({
    expiredLink,
    requestSessionStartedAt,
    selectedTimezone:
      last(autobookAvailabilityData?.docData?.selected)?.timeZone ??
      moment.tz.guess(),
    isRescheduleFlow: !!(prevSelectedSlot && prevSelectedSlot?.length > 0),
    selectedSlot: null,
    prevSelectedSlot,
    computedAvailability: [],
    formData: {
      firstName: autobookAvailabilityData?.docData?.candidate?.firstName,
      lastName: autobookAvailabilityData?.docData?.candidate?.lastName,
      email: autobookAvailabilityData?.docData?.candidate?.email,
      telephone: `${autobookAvailabilityData?.docData?.candidate?.countryCode}${autobookAvailabilityData?.docData?.candidate?.telephone}`
    }
  });

  actions.setProviders(autobookAvailabilityData?.docData?.providers ?? []);
  actions.setUiState({
    currentView: "month",
    currentStep:
      isRescheduleFlow && !isCancelled
        ? "scheduled"
        : isCancelled
          ? "scheduling_cancelled"
          : "initial"
  });
};

export const calculateRange = (
  autobookAvailabilityData: AvailabilityAutobookLinkDataApiResponse,
  availabilityTimezone: string,
  availabilityWindowMins: number
): MeetingAvailabilityRange[] => {
  let range: MeetingAvailabilityRange[] = [];

  if (
    autobookAvailabilityData?.docData?.selectionMode === "range" ||
    !autobookAvailabilityData?.docData?.v ||
    autobookAvailabilityData?.docData?.v === 1
  ) {
    const rangeStartMoment = moment(autobookAvailabilityData?.docData?.start)
      .utc()
      .tz(availabilityTimezone, true)
      .startOf("day");
    const rangeEndMoment = rangeStartMoment
      .clone()
      .add(availabilityWindowMins, "minutes");
    range = [
      {
        rangeStart: rangeStartMoment.format(),
        rangeEnd: rangeEndMoment.format()
      }
    ];
  }

  if (autobookAvailabilityData?.docData?.selectionMode === "individual_dates") {
    range =
      autobookAvailabilityData?.docData?.selectedDates?.map((date) => {
        const rangeStartMoment = moment(date)
          .utc()
          .tz(availabilityTimezone, true)
          .startOf("day");
        const rangeEndMoment = rangeStartMoment.clone().add(1, "day");
        return {
          rangeStart: rangeStartMoment.format(),
          rangeEnd: rangeEndMoment.format()
        };
      }) ?? [];
  }

  return range;
};
